import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    Flex,
    HStack,
    IconButton,
    keyframes,
    Portal,
    Text,
    Textarea,
    Tooltip,
    useColorModeValue,
} from "@chakra-ui/react";
import { TbTrash, TbX, TbSend, TbSparkles, TbRefresh } from "react-icons/tb";
import { useAiChat } from "Providers/AiChatProvider";
import { loadUsdToBrl } from "Helpers/Currency";
import MarkdownRenderer from "./MarkdownRenderer";

const bounce = keyframes`
    0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
    40% { transform: scale(1); opacity: 1; }
`;

const STATUS_MESSAGES = [
    "Pensando…",
    "Contando as estrelas no céu…",
    "Consultando as nuvens…",
    "Medindo o vento…",
    "Ouvindo os trovões…",
    "Juntando as ideias…",
    "Perguntando pro Sol…",
    "Cutucando as nuvens…",
    "Calibrando o termômetro…",
    "Interrogando a chuva…",
    "Domando os furacões…",
    "Farejando a próxima friagem…",
    "Negociando com o El Niño…",
    "Contando gotas de chuva…",
    "Alinhando os satélites…",
    "Peitando a frente fria…",
    "Decifrando os raios…",
    "Coletando orvalho…",
    "Perseguindo tempestades…",
    "Traduzindo o canto da cigarra…",
    "Consultando o joelho do vovô…",
    "Perguntando pro sapo se vai chover…",
    "Vendo se o São Pedro tá de bom humor…",
    "Molhando o dedo pra sentir o vento…",
    "Espiando o formigueiro…",
    "Vendo se a andorinha voa baixo…",
    "Cochichando com o cata-vento…",
    "Batendo um papo com o INMET…",
    "Lendo o horóscopo das nuvens…",
    "Provando a água da chuva…",
    "Contando raios por segundo…",
    "Torcendo pra não gear…",
    "Amansando o furacão…",
    "Chamando a chuva com reza brava…",
    "Conferindo o calo do reumatismo…",
    "Assoprando pra afastar a friagem…",
    "Consultando a sabedoria caipira…",
];

// Random status index, avoiding an immediate repeat of the current one.
function pickStatus(exclude) {
    if (STATUS_MESSAGES.length <= 1) return 0;
    let i;
    do {
        i = Math.floor(Math.random() * STATUS_MESSAGES.length);
    } while (i === exclude);
    return i;
}

const GROUPING_LABELS = {
    "30mins": "30 min",
    "4hours": "4 h",
    "1day": "diário",
    raw: "bruto",
};

function formatTokens(n) {
    if (n === undefined || n === null) return "0";
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return `${n}`;
}

function formatCost(usd) {
    if (!usd) return "US$ 0,0000";
    return `US$ ${Number(usd).toFixed(4).replace(".", ",")}`;
}

function formatBrl(brl) {
    return `R$ ${Number(brl || 0).toFixed(4).replace(".", ",")}`;
}

function formatShortDate(iso) {
    if (!iso) return "";
    const [, m, d] = iso.split("-");
    return d && m ? `${d}/${m}` : iso;
}

function formatPeriodTag(period) {
    if (!period) return null;
    const start = formatShortDate(period.start);
    const end = formatShortDate(period.end);
    const range = start === end ? start : `${start}–${end}`;
    const grouping = GROUPING_LABELS[period.grouping] || period.grouping;
    return grouping ? `${range} (${grouping})` : range;
}

function TypingIndicator() {
    const [statusIndex, setStatusIndex] = useState(() => pickStatus(-1));
    const dotColor = useColorModeValue("gray.500", "gray.300");

    useEffect(() => {
        const interval = setInterval(() => {
            setStatusIndex((i) => pickStatus(i));
        }, 2200);
        return () => clearInterval(interval);
    }, []);

    return (
        <HStack spacing={3} align="center">
            <HStack spacing={1}>
                {[0, 1, 2].map((i) => (
                    <Box
                        key={i}
                        w="6px"
                        h="6px"
                        borderRadius="full"
                        bg={dotColor}
                        animation={`${bounce} 1.4s ${i * 0.2}s infinite ease-in-out`}
                    />
                ))}
            </HStack>
            <Text fontSize="xs" color={dotColor}>
                {STATUS_MESSAGES[statusIndex]}
            </Text>
        </HStack>
    );
}

function AssistantMeta({ usage, context, brlRate }) {
    const metaColor = useColorModeValue("gray.500", "gray.400");
    const stations = context?.stations || [];
    const periodTag = formatPeriodTag(context?.period);

    const textParts = [];
    if (stations.length > 0) textParts.push(stations.map((s) => s.name).join(", "));
    if (periodTag) textParts.push(periodTag);
    if (usage) {
        textParts.push(`↑ ${formatTokens(usage.input_tokens)} · ↓ ${formatTokens(usage.output_tokens)} tokens`);
    }

    const usd = usage ? Number(usage.cost_usd) || 0 : null;

    if (textParts.length === 0 && usd === null) return null;

    const prefix = textParts.join(" · ");

    return (
        <Text mt={1} pl={1} fontSize="xs" color={metaColor}>
            {prefix}
            {usd !== null && prefix ? " · " : ""}
            {usd !== null &&
                (brlRate ? (
                    <Tooltip label={formatCost(usd)} placement="top" hasArrow>
                        <Text
                            as="span"
                            title={formatCost(usd)}
                            borderBottom="1px dashed"
                            borderColor="currentColor"
                            cursor="help"
                        >
                            {formatBrl(usd * brlRate)}
                        </Text>
                    </Tooltip>
                ) : (
                    <Text as="span">{formatCost(usd)}</Text>
                ))}
        </Text>
    );
}

export default function AiChatPopup() {
    const { isOpen, close, clear, messages, sending, sendMessage, retryLast } = useAiChat();
    const [input, setInput] = useState("");
    const [brlRate, setBrlRate] = useState(null);
    const listRef = useRef(null);
    const textareaRef = useRef(null);

    useEffect(() => {
        loadUsdToBrl().then(setBrlRate).catch(() => {});
    }, []);

    const overlayBg = useColorModeValue("blackAlpha.600", "blackAlpha.700");
    const panelBg = useColorModeValue("white", "gray.800");
    const headerBg = useColorModeValue("gray.50", "gray.900");
    const borderColor = useColorModeValue("gray.200", "gray.700");
    const assistantBubbleBg = useColorModeValue("gray.100", "gray.700");
    const assistantTextColor = useColorModeValue("gray.800", "gray.100");
    const errorBg = useColorModeValue("red.50", "red.900");
    const errorColor = useColorModeValue("red.600", "red.200");
    const emptyColor = useColorModeValue("gray.400", "gray.500");
    const footerBg = useColorModeValue("white", "gray.800");

    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
    }, [messages, sending, isOpen]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") close();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [isOpen, close]);

    const autoGrow = (el) => {
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    };

    const handleSend = () => {
        const text = input.trim();
        if (!text || sending) return;
        sendMessage(text);
        setInput("");
        // Keep the composer usable mid-send: reset its height and keep focus so
        // the user can start typing the next message without clicking back in.
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.focus();
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    if (!isOpen) return null;

    return (
        <Portal>
            <Flex
                position="fixed"
                inset={0}
                zIndex={2000}
                bg={overlayBg}
                align="center"
                justify="center"
                p={{ base: 3, md: 6 }}
                onClick={close}
            >
            <Box
                onClick={(e) => e.stopPropagation()}
                w="100%"
                h="100%"
                maxW="6xl"
                bg={panelBg}
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius={{ base: "lg", md: "2xl" }}
                boxShadow="2xl"
                display="flex"
                flexDirection="column"
                overflow="hidden"
            >
                {/* Header */}
                <Flex
                    align="center"
                    px={4}
                    py={3}
                    bg={headerBg}
                    borderBottomWidth="1px"
                    borderColor={borderColor}
                >
                    <Flex
                        align="center"
                        justify="center"
                        w="32px"
                        h="32px"
                        borderRadius="lg"
                        bg="brand.500"
                        color="white"
                        mr={3}
                    >
                        <TbSparkles size={18} />
                    </Flex>
                    <Box flex={1} minW={0}>
                        <Text fontSize="sm" fontWeight="bold" noOfLines={1}>
                            AgroIA WS
                        </Text>
                    </Box>
                    <Tooltip label="Limpar conversa" placement="top">
                        <IconButton
                            aria-label="Limpar conversa"
                            icon={<TbTrash />}
                            size="sm"
                            variant="ghost"
                            mr={1}
                            isDisabled={messages.length === 0 || sending}
                            onClick={clear}
                        />
                    </Tooltip>
                    <Tooltip label="Fechar" placement="top">
                        <IconButton
                            aria-label="Fechar"
                            icon={<TbX />}
                            size="sm"
                            variant="ghost"
                            onClick={close}
                        />
                    </Tooltip>
                </Flex>

                {/* Messages */}
                <Box ref={listRef} flex={1} overflowY="auto" px={4} py={3}>
                  <Box maxW="3xl" mx="auto" w="100%" minH="100%" display="flex" flexDirection="column">
                    {messages.length === 0 && !sending && (
                        <Flex direction="column" align="center" justify="center" flex={1} textAlign="center" px={4}>
                            <TbSparkles size={32} color="var(--chakra-colors-brand-500)" />
                            <Text mt={3} fontSize="sm" fontWeight="semibold">
                                Como posso ajudar?
                            </Text>
                            <Text mt={2} fontSize="xs" color={emptyColor} fontWeight="medium">
                                Especialista em clima e agricultura.
                            </Text>
                            <Text mt={1} fontSize="xs" color={emptyColor} maxW="280px">
                                Pergunte sobre temperatura, chuva, vento,
                                <br />
                                horas de frio, geadas ou qualquer dado
                                <br />
                                das estações.
                            </Text>
                        </Flex>
                    )}

                    {messages.map((msg, idx) => {
                        if (msg.role === "user") {
                            return (
                                <Flex key={idx} justify="flex-end" mb={3}>
                                    <Box
                                        maxW="85%"
                                        bg="brand.500"
                                        color="white"
                                        px={3}
                                        py={2}
                                        borderRadius="lg"
                                        borderBottomRightRadius="sm"
                                        fontSize="sm"
                                        whiteSpace="pre-wrap"
                                    >
                                        {msg.content}
                                    </Box>
                                </Flex>
                            );
                        }

                        if (msg.error) {
                            return (
                                <Box key={idx} mb={3}>
                                    <Box
                                        maxW="85%"
                                        bg={errorBg}
                                        color={errorColor}
                                        px={3}
                                        py={2}
                                        borderRadius="lg"
                                        borderBottomLeftRadius="sm"
                                        fontSize="sm"
                                    >
                                        {msg.content}
                                    </Box>
                                    <Button
                                        size="xs"
                                        variant="link"
                                        colorScheme="red"
                                        leftIcon={<TbRefresh />}
                                        mt={1}
                                        onClick={retryLast}
                                        isDisabled={sending}
                                    >
                                        Tentar novamente
                                    </Button>
                                </Box>
                            );
                        }

                        return (
                            <Box key={idx} mb={3}>
                                <Box
                                    maxW="90%"
                                    bg={assistantBubbleBg}
                                    color={assistantTextColor}
                                    px={3}
                                    py={2}
                                    borderRadius="lg"
                                    borderBottomLeftRadius="sm"
                                >
                                    <MarkdownRenderer>{msg.content}</MarkdownRenderer>
                                </Box>
                                <AssistantMeta usage={msg.usage} context={msg.context} brlRate={brlRate} />
                            </Box>
                        );
                    })}

                    {sending && (
                        <Box mb={3}>
                            <Box
                                display="inline-block"
                                bg={assistantBubbleBg}
                                px={3}
                                py={3}
                                borderRadius="lg"
                                borderBottomLeftRadius="sm"
                            >
                                <TypingIndicator />
                            </Box>
                        </Box>
                    )}
                  </Box>
                </Box>

                {/* Footer */}
                <Flex
                    px={3}
                    py={3}
                    borderTopWidth="1px"
                    borderColor={borderColor}
                    bg={footerBg}
                    justify="center"
                >
                  <Flex align="flex-end" maxW="3xl" w="100%">
                    <Textarea
                        ref={textareaRef}
                        value={input}
                        onChange={(e) => {
                            setInput(e.target.value);
                            autoGrow(e.target);
                        }}
                        onKeyDown={handleKeyDown}
                        placeholder="Escreva sua mensagem…"
                        size="sm"
                        rows={1}
                        resize="none"
                        minH="40px"
                        maxH="120px"
                        borderRadius="lg"
                        mr={2}
                    />
                    <IconButton
                        aria-label="Enviar"
                        icon={<TbSend />}
                        colorScheme="brand"
                        borderRadius="lg"
                        onClick={handleSend}
                        isLoading={sending}
                        isDisabled={!input.trim()}
                    />
                  </Flex>
                </Flex>
            </Box>
            </Flex>
        </Portal>
    );
}
